// @flow
import moment from 'moment';
// Time constants
import { PAY_FREQUENCY } from 'pages/AffordaWagesAndIncome/constants';

const WEEKS_PER_MONTH = 4.33; // Average weeks per month

// Pay period constants
const WEEKS_PER_YEAR = 52;
const MONTHS_PER_YEAR = 12;
const BIWEEKLY_PERIODS_PER_YEAR = 26;
const SEMIMONTHLY_PERIODS_PER_YEAR = 24;

// Default values
const DEFAULT_HOURS_WEEKS_MONTHS = 1;

export const getUniqueMemberIds = (items) => {
  return [...new Set(items.map((item) => item?.memberId))];
};

export const getMemberRecords = (items, memberId) => {
  return items.filter((item) => item.memberId === memberId);
};

export const getBasePayRate = ({
  payFrequency,
  weeklyAmount,
  monthlyAmount,
  annualAmount,
}): number | null => {
  let basePayRate = null;
  switch (payFrequency) {
    case PAY_FREQUENCY.weekly:
      basePayRate = Number(weeklyAmount) || 0;
      break;
    case PAY_FREQUENCY.monthly:
      basePayRate = Number(monthlyAmount) || 0;
      break;
    case PAY_FREQUENCY.annually:
      basePayRate = Number(annualAmount) || 0;
      break;
    default:
      break;
  }

  return basePayRate;
};

export const getTotalByFieldName = (items, fieldName) => {
  return items.reduce((sum, item) => sum + item[fieldName], 0);
};

export const getGrandTotal = ({ items, fieldId }) => {
  // Filter out members with no valid income items
  const validMemberIncomes = items.filter(
    (member) => member[fieldId].length > 0,
  );

  // Calculate grand total
  const grandTotal = getTotalByFieldName(validMemberIncomes, 'totalIncome');

  return {
    [fieldId]: validMemberIncomes,
    totalIncome: grandTotal,
  };
};

export const getHoursWeeksMonths = (items) => {
  const length = items?.length;

  if (Boolean(length)) {
    const hasMoreThanOneItem = length > 1;
    return hasMoreThanOneItem ? 1 : items[0]?.HoursWeekMonth || 0;
  }

  return 0;
};

// Boundary helpers
function clampValue(value, max) {
  return Math.min(Math.max(value, 1), max);
}

function getClampedPeriods(diffWeeks, periodType) {
  switch (periodType) {
    case PAY_FREQUENCY.weekly:
      return clampValue(diffWeeks, WEEKS_PER_YEAR);
    case PAY_FREQUENCY.biweekly:
      return clampValue(Math.floor(diffWeeks / 2), BIWEEKLY_PERIODS_PER_YEAR);
    case PAY_FREQUENCY.semimonthly:
      return clampValue(
        Math.ceil(diffWeeks / WEEKS_PER_MONTH) * 2,
        SEMIMONTHLY_PERIODS_PER_YEAR,
      );
    case PAY_FREQUENCY.monthly:
      return clampValue(
        Math.ceil(diffWeeks / WEEKS_PER_MONTH),
        MONTHS_PER_YEAR,
      );
    default:
      return DEFAULT_HOURS_WEEKS_MONTHS;
  }
}

function isWholeYear(startDate, endDate) {
  const evalStartDate = startDate
    ? moment(startDate)
    : moment().startOf('year');
  const evalEndDate = endDate ? moment(endDate) : moment().endOf('year');

  const yearStart = moment().startOf('year');
  const yearEnd = moment().endOf('year');
  return (
    evalStartDate.isSameOrBefore(yearStart) &&
    evalEndDate.isSameOrAfter(yearEnd)
  );
}

const isDate = (date: Date | null) => date instanceof Date;
const getWeeksDiff = (startDate: Date | null, endDate: Date | null) => {
  const start = isDate(startDate)
    ? moment(startDate)
    : moment().startOf('year');
  const end = isDate(endDate) ? moment(endDate) : moment().endOf('year');

  return Math.ceil(Math.abs(end.diff(start, 'weeks', true)));
};

export function getIncomeItem({
  id,
  memberId,
  payType,
  startDate,
  endDate,
  payFrequency,
  payRate,
  hoursPerWeek,
  excludeStartDate,
  excludeEndDate,
  seasonalWorkWeeks,
}) {
  // If seasonalWorkWeeks is defined, treat as partial year with specific weeks
  if (seasonalWorkWeeks) {
    const weeks = Number(seasonalWorkWeeks) || 0;

    switch (payFrequency) {
      case PAY_FREQUENCY.hourly: {
        const totalIncome = weeks * payRate * hoursPerWeek;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.weekly: {
        const totalIncome = weeks * payRate;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.biweekly: {
        const periods = Math.floor(weeks / 2);
        const totalIncome = periods * payRate * hoursPerWeek;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.semimonthly: {
        const months = Math.ceil(weeks / WEEKS_PER_MONTH);
        const periods = months * 2;
        const totalIncome = periods * payRate * hoursPerWeek;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.monthly: {
        const months = Math.ceil(weeks / WEEKS_PER_MONTH);
        const totalIncome = months * payRate;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.annually:
      default:
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: payRate,
        };
    }
  }

  // Original logic for non-seasonal calculations
  const isFullYear = isWholeYear(startDate, endDate);
  const isPartialYear =
    !isFullYear ||
    (isFullYear && excludeStartDate?.length && excludeEndDate?.length);

  // For annual frequency or partial year calculations
  if (payFrequency === PAY_FREQUENCY.annually || isPartialYear) {
    if (payFrequency === PAY_FREQUENCY.annually) {
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.annually,
        payRate,
        HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
        incomeTotal: payRate,
      };
    }

    // Calculate weeks for partial year
    const diffWeeks = getWeeksDiff(startDate, endDate);

    // For partial year, convert everything to annual
    switch (payFrequency) {
      case PAY_FREQUENCY.hourly: {
        const periods = getClampedPeriods(diffWeeks, PAY_FREQUENCY.weekly);
        const totalIncome = hoursPerWeek * payRate * periods;

        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.weekly: {
        const periods = getClampedPeriods(diffWeeks, PAY_FREQUENCY.weekly);
        const totalIncome = payRate * periods;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.biweekly: {
        const periods = getClampedPeriods(diffWeeks, PAY_FREQUENCY.biweekly);
        const totalIncome = hoursPerWeek * payRate * periods;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.semimonthly: {
        const periods = getClampedPeriods(diffWeeks, PAY_FREQUENCY.semimonthly);
        const totalIncome = hoursPerWeek * payRate * periods;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate: totalIncome,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: totalIncome,
        };
      }
      case PAY_FREQUENCY.monthly: {
        const periods = getClampedPeriods(diffWeeks, PAY_FREQUENCY.monthly);
        const totalIncome = payRate * periods;
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.monthly,
          payRate,
          HoursWeekMonth: periods,
          incomeTotal: totalIncome,
        };
      }
      default:
        return {
          id,
          memberId,
          payType,
          payFrequency: PAY_FREQUENCY.annually,
          payRate,
          HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
          incomeTotal: payRate,
        };
    }
  }

  // Full year calculations
  switch (payFrequency) {
    case PAY_FREQUENCY.hourly:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.hourly,
        payRate,
        HoursWeekMonth: hoursPerWeek,
        incomeTotal: hoursPerWeek * payRate * WEEKS_PER_YEAR,
      };
    case PAY_FREQUENCY.weekly:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.weekly,
        payRate,
        HoursWeekMonth: WEEKS_PER_YEAR,
        incomeTotal: payRate * WEEKS_PER_YEAR,
      };
    case PAY_FREQUENCY.biweekly:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.biweekly,
        payRate,
        HoursWeekMonth: hoursPerWeek,
        incomeTotal: hoursPerWeek * payRate * BIWEEKLY_PERIODS_PER_YEAR,
      };
    case PAY_FREQUENCY.semimonthly:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.semimonthly,
        payRate,
        HoursWeekMonth: hoursPerWeek,
        incomeTotal: hoursPerWeek * payRate * SEMIMONTHLY_PERIODS_PER_YEAR,
      };
    case PAY_FREQUENCY.monthly:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.monthly,
        payRate,
        HoursWeekMonth: MONTHS_PER_YEAR,
        incomeTotal: payRate * MONTHS_PER_YEAR,
      };
    case PAY_FREQUENCY.annually:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.annually,
        payRate,
        HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
        incomeTotal: payRate,
      };
    default:
      return {
        id,
        memberId,
        payType,
        payFrequency: PAY_FREQUENCY.annually,
        payRate,
        HoursWeekMonth: DEFAULT_HOURS_WEEKS_MONTHS,
        incomeTotal: payRate,
      };
  }
}
